import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import Alert from 'react-bootstrap/Alert';
import {Colors} from '../constants/colors'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {
  BankAccountModel, CreditorConnectionModel, UpdatePayoutCreditFacilityModel,
} from '../redux/types'
import ConfirmModal from '../components/ConfirmModal'
import BankAccountSelectorModal from '../components/BankAccount/BankAccountSelectorModal'
import {formatDateTimeString} from '../utils/dateHelper'
import {useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import Divider from '../components/Divider';
import CreditorConnectionList from '../components/CreditorConnections/EmployerCreditorList';
import AddCreditorConnectionModal from '../components/CreditorConnections/AddEmployerCreditorModal';
import {faArrowLeft, faPen} from "@fortawesome/free-solid-svg-icons";
import NavLink from '../components/NavLink';
import {titleMarginBottom} from '../constants/layout';
import Link from '../components/Link';
import usePayoutCreditFacilityActions from '../hooks/usePayoutCreditFacilityActions';
import EditPayoutCreditFacilityModal from '../components/EditPayoutCreditFacilityModal';
import InvoicePayoutListComponent from '../components/CreditFacilityPayoutList';
import SilenceCreditSpaceWarningModal from '../components/CreditSpace/SilenceCreditSpaceWarningModal';
import {formatMoney} from '../utils/moneyHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StateBadge from '../components/StateBadge';
import useMarketBankAccounts from '../hooks/useMarketBankAccounts';
import { formatBankAccountName } from '../components/BankAccount/BankAccountSelect';

const PayoutCreditFacilityDetails = () => {
  const styles = useStyles()
  const params = useParams<{id: string}>()
  const navigate = useNavigate();

  const [addCreditorModalVisible, setAddCreditorModalVisible] = useState(false)
  const [confirmDeleteCreditorModalVisible, setConfirmDeleteCreditorModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [bankAccountSelectorModalVisible, setBankAccountSelectorModalVisible] = useState(false)
  const [confirmSetBankAccountModalVisible, setConfirmSetBankAccountModalVisible] = useState(false)
  const [silenceCreditSpaceWarningModalVisible, setSilenceCreditSpaceWarningModalVisible] = useState(false)
  const [creditorDeleteCandidate, setCreditorDeleteCandidate] = useState<CreditorConnectionModel | null>(null)
  const [bankAccountCandidate, setBankAccountCandidate] = useState<BankAccountModel | null>(null)

  const [errorDismissed, setErrorDismissed] = useState(false)

  const loading = useAppSelector(state => state.payoutCreditFacility.loading)
  const error = useAppSelector(state => state.payoutCreditFacility.error)
  const payoutCreditFacility = useAppSelector(state => state.payoutCreditFacility.facilityDetails)

  const bankAccounts = useMarketBankAccounts(payoutCreditFacility?.marketId)

  const {
    getPayoutCreditFacilityDetails,
    updatePayoutCreditFacility,
    setRepaymentBankAccount,
    silenceCreditSpaceWarning,
    addCreditor,
    deleteCreditor
  } = usePayoutCreditFacilityActions()

  useEffect(() => {
    if ((payoutCreditFacility?.id == null || payoutCreditFacility.id !== params.id) && params.id) {
      getPayoutCreditFacilityDetails(params.id || '')
    }
  }, [])

  const handleSilenceCreditSpaceWarning = (days: number) => {
    setSilenceCreditSpaceWarningModalVisible(false)
    silenceCreditSpaceWarning(payoutCreditFacility?.id as string, days)
  }

  const handleDeleteCreditor = (creditor: CreditorConnectionModel) => {
    setCreditorDeleteCandidate(creditor)
    setConfirmDeleteCreditorModalVisible(true)
  }

  const handleConfirmDeleteCreditor = () => {
    deleteCreditor(payoutCreditFacility?.id as string, creditorDeleteCandidate?.id || '')
    setCreditorDeleteCandidate(null)
    setConfirmDeleteCreditorModalVisible(false)
  }

  const handleAddCreditor = (creditor: CreditorConnectionModel) => {
    addCreditor(payoutCreditFacility?.id as string, creditor)
    setAddCreditorModalVisible(false)
  }

  useEffect(() => {
    setErrorDismissed(false)
  }, [error])

  const handleUpdate = (updateModel: UpdatePayoutCreditFacilityModel) => {
    updatePayoutCreditFacility(payoutCreditFacility?.id as string, updateModel)
    setEditModalVisible(false)
  }

  const handleBankAccountSelected = (bankAccount: BankAccountModel) => {
    setBankAccountCandidate(bankAccount)
    setConfirmSetBankAccountModalVisible(true)
    setBankAccountSelectorModalVisible(false)
  }

  const handleSetBankAccountConfirmed = () => {
    if (bankAccountCandidate) {
      setRepaymentBankAccount(payoutCreditFacility?.id as string, bankAccountCandidate.bankAccountId)
    }

    setConfirmSetBankAccountModalVisible(false)
    setBankAccountCandidate(null)
  }

  const renderError = () => {
    if (!error || errorDismissed) return null

    return (
      <Alert key='facilityError' variant='danger' onClose={() => setErrorDismissed(true)} dismissible>
        {error instanceof Error ? error.message : error}
      </Alert>
    )
  }

  const renderRepaymentBankAccount = () => {
    if (!payoutCreditFacility?.repaymentBankAccountId) return 'Not specified'

    const bankAccount = bankAccounts.find(x => x.bankAccountId === payoutCreditFacility.repaymentBankAccountId)
    return bankAccount ? formatBankAccountName(bankAccount.name, bankAccount.bban) : payoutCreditFacility.repaymentBankAccountId
  }

  const renderGeneralInformationCardBody = () => {
    return (
      <div>
        <p>Customer: <Link to={`/customers/${payoutCreditFacility?.customerId}`}>{payoutCreditFacility?.customerName}</Link></p>
        <p>Currency: <span>{payoutCreditFacility?.currency}</span></p>
        <p>Launch date: <span>{payoutCreditFacility?.launchDate != null ? formatDateTimeString(payoutCreditFacility?.launchDate) : 'Not specified'}</span></p>
        <p>Allowed origins: <span>{payoutCreditFacility?.allowedOrigins?.join(', ') || 'None'}</span></p>
      </div>
    )
  }

  // A facility with no payout settings reports null for every limit. Zero is a real setting here - a max credit
  // per user of zero pays nobody anything - so an unset limit has to read differently from one set to zero.
  const renderSetting = (value: number | null | undefined) => value ?? 'Not set'

  const renderPayoutSettingsCardBody = () => {
    return (
      <div>
        <p>Min. payout amount: <span>{renderSetting(payoutCreditFacility?.minimumPayoutAmount)}</span></p>
        <p>Max. payout amount: <span>{renderSetting(payoutCreditFacility?.maximumPayoutAmount)}</span></p>
        <p>Min. days until repayment date: <span>{renderSetting(payoutCreditFacility?.minimumDaysUntilRepaymentDate)}</span></p>
        <p>Max. days until repayment date: <span>{renderSetting(payoutCreditFacility?.maximumDaysUntilRepaymentDate)}</span></p>
        <p>Max. credit per user: <span>{renderSetting(payoutCreditFacility?.maxCreditPerUser)}</span></p>
      </div>
    )
  }

  const renderCreditCardBody = () => {
    return (
      <div>
        <p>Credit limit: <span>{formatMoney(payoutCreditFacility?.creditLimit)}</span></p>
        <p>Credit usage: <span>{formatMoney(payoutCreditFacility?.creditUsage)}</span></p>
        <p>Remaining credit space warning: <span>{payoutCreditFacility?.remainingCreditSpaceWarningPercent}%</span></p>
        <p>Credit space warning silenced until: <span>{formatDateTimeString(payoutCreditFacility?.creditSpaceWarningSilencedUntilDate)}</span>
          <FontAwesomeIcon
            onClick={() => setSilenceCreditSpaceWarningModalVisible(true)}
            className={styles.inlineIcon}
            icon={faPen}
            color={Colors.euro_600}
          />
        </p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <h1>Payout credit facility: {payoutCreditFacility?.customerName}</h1>
        <StateBadge state={payoutCreditFacility?.state} />
      </div>
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)} />
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>
        </div>
      </div>
      {renderError()}
      <CardSection>
        <DetailsCard onClick={() => setEditModalVisible(true)}>
          <h6>General information</h6>
          {renderGeneralInformationCardBody()}
        </DetailsCard>
        <DetailsCard onClick={!payoutCreditFacility?.repaymentBankAccountId ? () => setBankAccountSelectorModalVisible(true) : undefined}>
          <h6>Bank information</h6>
          <p>Repayment bank account: <span>{renderRepaymentBankAccount()}</span></p>
        </DetailsCard>
        <DetailsCard onClick={() => setEditModalVisible(true)}>
          <h6>Payout settings</h6>
          {renderPayoutSettingsCardBody()}
        </DetailsCard>
        <div className={styles.cardColumnContainer}>
          <DetailsCard onClick={() => setEditModalVisible(true)}>
            <h6>Credit information</h6>
            {renderCreditCardBody()}
          </DetailsCard>
          <DetailsCard iconType='Add' onClick={() => setAddCreditorModalVisible(true)}>
            <h6>Creditors</h6>
            <CreditorConnectionList
              creditors={payoutCreditFacility?.creditors ?? []}
              onDeleteClick={handleDeleteCreditor}
            />
          </DetailsCard>
        </div>
      </CardSection>
      <CardSection>
        <DetailsCard >
          <h6>Payouts</h6>
          <InvoicePayoutListComponent payoutCreditFacilityId={payoutCreditFacility?.id} />
        </DetailsCard>
      </CardSection>
      <Divider height={50}/>
      <ConfirmModal
        visible={confirmSetBankAccountModalVisible}
        confirmText='Are you sure you want to connect this bank account? This cannot be changed.'
        onConfirmClick={handleSetBankAccountConfirmed}
        onCancelClick={() => {
          setConfirmSetBankAccountModalVisible(false)
          setBankAccountCandidate(null)
        }}
      />
      <ConfirmModal
        visible={confirmDeleteCreditorModalVisible}
        confirmText={`Are you sure you want to remove ${creditorDeleteCandidate?.name} as a creditor?`}
        onConfirmClick={handleConfirmDeleteCreditor}
        onCancelClick={() => setConfirmDeleteCreditorModalVisible(false)}
      />
      <AddCreditorConnectionModal
        visible={addCreditorModalVisible}
        marketId={payoutCreditFacility?.marketId ?? ''}
        existingCreditorConnectionModels={payoutCreditFacility?.creditors ?? []}
        onAddClick={handleAddCreditor}
        onCancelClick={() => setAddCreditorModalVisible(false)}
      />
      <BankAccountSelectorModal
        marketId={payoutCreditFacility?.marketId ?? ''}
        visible={bankAccountSelectorModalVisible}
        onSelectClick={handleBankAccountSelected}
        onCancelClick={() => setBankAccountSelectorModalVisible(false)}
      />
      <EditPayoutCreditFacilityModal
        visible={editModalVisible}
        initialValue={payoutCreditFacility ?? undefined}
        onCancelClick={() => setEditModalVisible(false)}
        onSaveClick={handleUpdate}
      />
      <SilenceCreditSpaceWarningModal
        visible={silenceCreditSpaceWarningModalVisible}
        onCancelClick={() => setSilenceCreditSpaceWarningModalVisible(false)}
        onSaveClick={handleSilenceCreditSpaceWarning}
      />
      <FullScreenLoadingIndicator visible={loading} />
    </div>
  )
}

export default PayoutCreditFacilityDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h5': {
      fontFamily: 'Aestetico-Regular',
      color: Colors.darkDefault,
    }
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10,
    alignItems: 'center',
    marginBottom: titleMarginBottom,
    '& h1': {
      marginBottom: 0
    }
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%'
  },
  navLinkContainer: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10
  },
  cardColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10
  },
  largeButton: {
    fontSize: "xxx-large",
    alignSelf: "center",
    margin: "20px",
    flexGrow: 1,
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  },
  inlineIcon: {
    marginLeft: 5,
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  }
})
